import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import AppInput from '../atoms/AppInput';
import AppButton from '../atoms/AppButton';
import InfoHint from '../atoms/InfoHint';
import ImageDropzone from '../molecules/ImageDropzone';
import ItemsSummary from '../molecules/ItemsSummary';
import NestedItemsModal from './NestedItemsModal';
import { useToast } from '../contexts/ToastContext';
import { parseDecimalInput, sanitizeDecimalInput } from '../utils/currency';
import { titleCase } from '../utils/text';
import {
  createProduct, updateProduct,
  createVariant, updateVariant, deleteVariant,
  createProductAddon, updateProductAddon, deleteProductAddon,
} from '../api/menuApi';

const IMMEDIATE_SAVE_HINT = 'As alterações aqui são salvas na hora, sem precisar salvar o produto.';

function ProductFormModal({ show, onHide, product, categories, defaultCategoryId, nextSortOrder, onSaved }) {
  const [saved, setSaved] = useState(product);
  const [name, setName] = useState(product?.name || '');
  const [description, setDescription] = useState(product?.description || '');
  // Vírgula, não ponto: o campo agora é de texto e é assim que se escreve preço
  // em português — o parse aceita as duas formas na volta.
  const [price, setPrice] = useState(product?.price != null ? String(product.price).replace('.', ',') : '');
  const [imageUrl, setImageUrl] = useState(product?.imageUrl || '');
  const [categoryId, setCategoryId] = useState(product?.categoryId || defaultCategoryId || categories[0]?.id || '');
  const [isAvailable, setIsAvailable] = useState(product?.isAvailable ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [subModal, setSubModal] = useState(null);
  const bodyRef = useRef(null);
  const formRef = useRef(null);
  const toast = useToast();

  const isEditing = !!saved;
  const variants = saved?.variants || [];
  const addons = saved?.addons || [];
  const hasVariants = variants.length > 0;
  const category = categories.find((c) => c.id === categoryId);
  // O meio a meio é liberado pela categoria; sem isso, o controle por variação
  // não teria efeito nenhum e só poluiria a lista.
  const allowsHalfAndHalf = !!category?.allowsHalfAndHalf;
  // Adicionais da categoria valem pra todo produto dela, mas são cadastrados
  // em outra tela (editar categoria) — sem este aviso, quem só olha o produto
  // não tem como saber que eles existem e continuam valendo aqui também.
  const categoryAddons = category?.addons || [];

  // Em tela cheia, com o formulário rolado, o aviso no topo do corpo fica fora
  // de vista: quem toca em Salvar vê o botão não fazer nada. O erro do servidor
  // traz o corpo de volta ao topo; o de validação leva ao campo culpado, que
  // além de tudo é onde o teclado precisa abrir.
  useEffect(() => {
    if (error) bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [error]);

  useEffect(() => {
    if (!Object.keys(fieldErrors).length) return;
    const firstInvalid = formRef.current?.querySelector('.is-invalid');
    if (!firstInvalid) return;
    firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
    firstInvalid.focus({ preventScroll: true });
  }, [fieldErrors]);

  function validate() {
    const errs = {};
    if (!name.trim()) errs.name = 'Informe o nome do produto.';
    if (!categoryId) errs.categoryId = 'Escolha uma categoria.';
    // Com variações o campo está desabilitado e o preço vem delas — cobrar um
    // valor aqui travaria o salvamento num campo que não dá para corrigir.
    if (!hasVariants && !(parseDecimalInput(price) > 0)) {
      errs.price = 'Informe um preço maior que zero.';
    }
    setFieldErrors(errs);
    return !Object.keys(errs).length;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    // Com variações o campo fica desabilitado e pode estar vazio; o preço do
    // produto não é usado nesse caso, mas a API espera um número.
    const priceValue = parseDecimalInput(price);
    // A ordem não vai no payload de edição: ela é definida arrastando no
    // cardápio, e reenviá-la aqui desfaria o arrasto. Produto novo entra no fim
    // da categoria escolhida.
    const payload = {
      categoryId,
      name: titleCase(name.trim()),
      description: description.trim() || null,
      price: Number.isFinite(priceValue) ? priceValue : 0,
      imageUrl: imageUrl.trim(),
      isAvailable,
    };
    try {
      if (isEditing) {
        const updated = await updateProduct(saved.id, payload);
        setSaved(updated);
        toast.success('Produto atualizado com sucesso!');
      } else {
        const created = await createProduct({ ...payload, sortOrder: nextSortOrder(categoryId) });
        setSaved(created);
        toast.success('Produto criado com sucesso!');
      }
      onSaved();
    } catch (e2) {
      setError(e2.message);
      toast.error(e2.message);
    } finally {
      setSaving(false);
    }
  }

  // Variações/adicionais só podem ser criados presos a um productId. Em vez
  // de obrigar o usuário a salvar o produto antes (fluxo antigo), criamos o
  // produto por baixo dos panos com o que já foi preenchido — preço fica 0 se
  // ainda não informado, já que ele pode vir só das variações — e abrimos o
  // submodal direto. Preço não é exigido aqui de propósito: exigi-lo bloquearia
  // justamente quem quer configurar variações porque não vai usar o preço único.
  async function ensureSavedForManage() {
    if (isEditing) return true;
    const errs = {};
    if (!name.trim()) errs.name = 'Informe o nome do produto.';
    if (!categoryId) errs.categoryId = 'Escolha uma categoria.';
    if (Object.keys(errs).length) {
      setFieldErrors((p) => ({ ...p, ...errs }));
      return false;
    }
    setSaving(true);
    setError(null);
    const priceValue = parseDecimalInput(price);
    try {
      const created = await createProduct({
        categoryId,
        name: titleCase(name.trim()),
        description: description.trim() || null,
        price: Number.isFinite(priceValue) && priceValue > 0 ? priceValue : 0,
        imageUrl: imageUrl.trim(),
        isAvailable,
        sortOrder: nextSortOrder(categoryId),
      });
      setSaved(created);
      return true;
    } catch (e2) {
      setError(e2.message);
      toast.error(e2.message);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleManage(target) {
    const ok = await ensureSavedForManage();
    if (ok) setSubModal(target);
  }

  function handleClose() {
    onSaved();
    onHide();
  }

  return (
    <>
      {/* enforceFocus desligado enquanto um submodal está aberto: senão o modal
          de trás rouba o foco dos campos do submodal. */}
      {/* scrollable mantém cabeçalho e rodapé fixos: sem isso o modal cresce
          junto com o conteúdo e o salvar só aparece no fim da rolagem. */}
      {/* Tela cheia até o tablet: um cartão centrado num celular sobra pouco
          espaço para o formulário e o teclado cobre o rodapé. Em tela cheia o
          scrollable mantém o Salvar sempre visível acima do teclado. */}
      <Modal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        scrollable
        fullscreen="md-down"
        enforceFocus={!subModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Editar produto' : 'Novo produto'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body ref={bodyRef} style={{ padding: '20px 24px' }}>
          {error && (
            <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: 13, borderRadius: 8 }}>
              {error}
            </Alert>
          )}

          <Form onSubmit={handleSubmit} id="product-form" ref={formRef} noValidate>
            <AppInput
              label="Nome"
              value={name}
              onChange={(e) => { setName(e.target.value); setFieldErrors((p) => ({ ...p, name: null })); }}
              placeholder="Ex: X-Burguer Clássico"
              error={fieldErrors.name}
              autoCapitalize="sentences"
              autoCorrect="off"
              autoComplete="off"
              enterKeyHint="next"
              required
            />

            <Form.Group className="mb-3">
              <Form.Label>Descrição</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ingredientes, detalhes do preparo..."
                autoCapitalize="sentences"
                enterKeyHint="next"
              />
            </Form.Group>

            <div className="form-row-split">
              <div className="flex-grow-1">
                <Form.Group className="mb-3">
                  <Form.Label>Categoria</Form.Label>
                  {/* Select nativo de propósito: no celular ele abre a roleta
                      do sistema, que é mais rápida e familiar que qualquer
                      dropdown desenhado por nós. */}
                  <Form.Select
                    value={categoryId}
                    onChange={(e) => { setCategoryId(e.target.value); setFieldErrors((p) => ({ ...p, categoryId: null })); }}
                    isInvalid={!!fieldErrors.categoryId}
                    required
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
                    ))}
                  </Form.Select>
                  {fieldErrors.categoryId && (
                    <Form.Control.Feedback type="invalid">{fieldErrors.categoryId}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
              <div className="form-row-split-narrow">
                {/* Com variações o preço do produto não é usado em lugar nenhum;
                    desabilitar diz isso melhor que uma dica embaixo de um campo
                    editável. O aviso vai num InfoHint logo abaixo, e não num
                    `title`: tooltip não existe onde não há mouse.
                    `inputMode="decimal"` em vez de `type="number"` — o teclado
                    numérico é o mesmo, sem as setas de incremento nem a roda do
                    mouse alterando o valor sem querer. */}
                <AppInput
                  label="Preço (R$)"
                  type="text"
                  inputMode="decimal"
                  value={price}
                  onChange={(e) => {
                    setPrice(sanitizeDecimalInput(e.target.value));
                    setFieldErrors((p) => ({ ...p, price: null }));
                  }}
                  disabled={hasVariants}
                  error={fieldErrors.price}
                  placeholder="0,00"
                  autoComplete="off"
                  enterKeyHint="done"
                  required
                />
              </div>
            </div>
            {hasVariants && (
              <InfoHint style={{ marginTop: -8 }}>
                O preço vem das variações — cada uma tem o seu.
              </InfoHint>
            )}

            <Form.Group className="mb-3 mt-3">
              <Form.Label>Imagem do produto</Form.Label>
              <ImageDropzone value={imageUrl} onChange={setImageUrl} actionsLayout="inline" />
            </Form.Group>

            <Form.Group>
              <Form.Label>Disponibilidade</Form.Label>
              <Form.Check
                type="switch"
                className="mt-1"
                label={isAvailable ? 'Disponível no cardápio' : 'Oculto do cardápio'}
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
              />
            </Form.Group>
          </Form>

          <div className="items-summary-group">
            <ItemsSummary
              title="Variações"
              items={variants}
              emptyLabel="Sem variações — o produto é vendido a preço único."
              manageLabel={hasVariants ? 'Gerenciar' : 'Adicionar'}
              onManage={() => handleManage('variants')}
              flagField={allowsHalfAndHalf ? 'allowsHalfAndHalf' : null}
              flagOffLabel="sem ½"
            />
            <ItemsSummary
              title="Adicionais"
              items={addons}
              emptyLabel="Sem adicionais próprios deste produto."
              manageLabel={addons.length ? 'Gerenciar' : 'Adicionar'}
              onManage={() => handleManage('addons')}
            />
            {categoryAddons.length > 0 && (
              <InfoHint style={{ marginTop: -6 }}>
                + {categoryAddons.length} {categoryAddons.length > 1 ? 'adicionais' : 'adicional'} da categoria "{category.name}" também {categoryAddons.length > 1 ? 'valem' : 'vale'} aqui: {categoryAddons.map((a) => a.name).join(', ')}.
              </InfoHint>
            )}
          </div>
        </Modal.Body>

        {/* Salvar volta para o rodapé: no meio do corpo ele parecia encerrar o
            modal, e o que vinha depois lia como outra tela. */}
        <Modal.Footer>
          {/* Neutro, não vermelho: fechar não destrói nada — o vermelho ficava
              mais chamativo que o próprio salvar. */}
          <AppButton variant="ghost" onClick={handleClose}>Fechar</AppButton>
          <AppButton type="submit" form="product-form" variant="primary" disabled={saving}>
            {saving ? 'Salvando...' : isEditing ? 'Salvar alterações' : 'Criar produto'}
          </AppButton>
        </Modal.Footer>
      </Modal>

      {isEditing && (
        <>
          <NestedItemsModal
            show={subModal === 'variants'}
            onHide={() => setSubModal(null)}
            title={`Variações de ${saved.name}`}
            subtitle={
              allowsHalfAndHalf
                ? `Opções com preço próprio — tamanhos (P, M, G), volumes (300ml), o que for. Use o ½ para tirar uma variação do meio a meio. ${IMMEDIATE_SAVE_HINT}`
                : `Opções com preço próprio — tamanhos (P, M, G), volumes (300ml), o que for. ${IMMEDIATE_SAVE_HINT}`
            }
            addLabel="Adicionar variação"
            emptyLabel="Nenhuma variação cadastrada."
            itemLabel="Variação"
            feminine
            flag={allowsHalfAndHalf ? {
              field: 'allowsHalfAndHalf',
              label: '½',
              onTitle: 'Aceita meio a meio — clique para desativar nesta variação',
              offTitle: 'Não aceita meio a meio — clique para ativar nesta variação',
            } : null}
            items={variants}
            onCreate={(data) => createVariant(saved.id, data)}
            onUpdate={(id, data) => updateVariant(saved.id, id, data)}
            onDelete={(id) => deleteVariant(saved.id, id)}
            onChange={(next) => setSaved((prev) => ({ ...prev, variants: next }))}
          />

          <NestedItemsModal
            show={subModal === 'addons'}
            onHide={() => setSubModal(null)}
            title={`Adicionais de ${saved.name}`}
            subtitle={`Itens opcionais que o cliente soma ao pedido. ${IMMEDIATE_SAVE_HINT}`}
            addLabel="Adicionar adicional"
            emptyLabel="Nenhum adicional cadastrado para este produto."
            itemLabel="Adicional"
            allowFree
            items={addons}
            onCreate={(data) => createProductAddon(saved.id, data)}
            onUpdate={(id, data) => updateProductAddon(saved.id, id, data)}
            onDelete={(id) => deleteProductAddon(saved.id, id)}
            onChange={(next) => setSaved((prev) => ({ ...prev, addons: next }))}
          />
        </>
      )}
    </>
  );
}

export default ProductFormModal;
